import React, { useEffect, useMemo, useState } from 'react';
import { ImageOff, EyeOff, ShieldAlert, UserCog, Check, Search, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { coverImageFor } from '@/lib/roles';
import { tradeLabel, UserType } from '@/data/constants';

interface Row {
  [k: string]: any;
}

const ROLES: UserType[] = ['homeowner', 'gc', 'subcontractor'];

/**
 * PII patterns that must NEVER appear in public listing text.
 *
 * The public feed hides city, ZIP, address and the whole contact block, but a
 * poster can still type a phone number or an email into the free-text title or
 * description — which bypasses the lead lock entirely. Any match is surfaced
 * here so an admin can edit or unpublish the listing.
 */
const PHONE_RE = /(\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/;
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;

/** Returns a list of "field: matched value" strings, empty when clean. */
export const piiHits = (l: { title?: string | null; description?: string | null }): string[] => {
  const hits: string[] = [];
  const check = (field: string, value?: string | null) => {
    if (!value) return;
    const phone = value.match(PHONE_RE);
    if (phone) hits.push(`${field}: ${phone[0].trim()}`);
    const email = value.match(EMAIL_RE);
    if (email) hits.push(`${field}: ${email[0].trim()}`);
  };
  check('title', l.title);
  check('description', l.description);
  return hits;
};

type StatusFilter = 'all' | 'published' | 'removed';

/**
 * Accent- and case-insensitive text key used by the moderation search.
 * An admin working in Spanish types "plomeria" or "Plomería" and both must find
 * the same listing, so diacritics are stripped on BOTH the query and the text.
 */
const norm = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();


/**
 * Admin-only moderation surfaces.
 *  - Listings & Images: search every listing (title, city, state, trade or the
 *    poster's email), remove a single photo, unpublish one listing, or select
 *    several and unpublish them in one action (status = 'removed'). Unpublished
 *    listings vanish from the home page, the marketplace and search instantly,
 *    but nothing is deleted — "Restore" puts a listing straight back.
 *    Every action is written to mkt_activity. All writes are additionally gated
 *    by the is_admin RLS policies on mkt_listings / mkt_activity.
 *  - Role audit: every mkt_profiles row grouped by user_type with one-click
 *    correction (profile row + auth metadata via the admin-set-role function).
 */
export const ListingsModeration: React.FC = () => {
  const { t, lang } = useI18n();
  const { profile } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  /** owner_id -> account email, so an admin can recognise who posted what. */
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [msg, setMsg] = useState('');

  useEffect(() => {
    supabase
      .from('mkt_listings')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500)
      .then(({ data }) => setRows(data || []));

    // Admins can read every profile (mkt_is_admin RLS), so map owner -> email.
    supabase
      .from('mkt_profiles')
      .select('id,email')
      .then(({ data }) => {
        const map: Record<string, string> = {};
        (data || []).forEach((p: Row) => {
          if (p.id) map[p.id] = p.email;
        });
        setEmails(map);
      });
  }, []);

  const log = (action: string, detail: string) =>
    supabase.from('mkt_activity').insert({ user_id: profile?.id || null, action, detail });

  const removePhoto = async (l: Row, url: string) => {
    setBusy(l.id);
    const next = (l.photos || []).filter((p: string) => p !== url);
    await supabase.from('mkt_listings').update({ photos: next }).eq('id', l.id);
    await log('admin_photo_removed', `${l.title} — ${url.slice(-32)}`);
    setRows((p) => p.map((x) => (x.id === l.id ? { ...x, photos: next } : x)));
    setBusy(null);
  };

  const setStatus = async (l: Row, status: string) => {
    setBusy(l.id);
    setMsg('');
    await supabase.from('mkt_listings').update({ status }).eq('id', l.id);
    await log(
      status === 'removed' ? 'admin_listing_removed' : 'admin_listing_restored',
      `${l.title} (${l.city}, ${l.state})`,
    );
    setRows((p) => p.map((x) => (x.id === l.id ? { ...x, status } : x)));
    setBusy(null);
  };

  /** Unpublish every checked listing in one pass, then clear the selection. */
  const bulkUnpublish = async () => {
    const ids = [...selected].filter((id) => rows.find((r) => r.id === id)?.status !== 'removed');
    if (!ids.length) return;
    setBusy('bulk');
    setMsg('');
    await supabase.from('mkt_listings').update({ status: 'removed' }).in('id', ids);
    await log('admin_listing_bulk_removed', `${ids.length} listing(s)`);
    setRows((p) => p.map((x) => (ids.includes(x.id) ? { ...x, status: 'removed' } : x)));
    setSelected(new Set());
    // t() interpolates {n} itself, so the sentence reads correctly in both languages.
    setMsg(t('admin.modBulkDone', { n: ids.length }));
    setBusy(null);
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /**
   * Translate the raw DB status ('published' / 'removed') for the badge, so a
   * Spanish admin never sees an untranslated English word on the card.
   * Unknown values fall through to the raw string rather than blanking out.
   */
  const statusLabel = (status?: string | null) => {
    if (status === 'published') return t('admin.modStatusPublished');
    if (status === 'removed') return t('admin.modStatusRemoved');
    return status || '—';
  };

  const visible = useMemo(() => {
    const q = norm(query);
    return rows.filter((l) => {
      if (statusFilter === 'published' && l.status !== 'published') return false;
      if (statusFilter === 'removed' && l.status !== 'removed') return false;
      if (!q) return true;
      // The card shows the trade in the active language, so both the English and
      // the Spanish label are searchable ("plumbing" AND "plomería" both hit).
      // norm() also strips accents, so "plomeria" finds "Plomería".
      const haystack = norm(
        [
          l.title,
          l.city,
          l.state,
          l.trade,
          tradeLabel(l.trade, 'en'),
          tradeLabel(l.trade, 'es'),
          l.slug,
          l.description,
          emails[l.owner_id] || '',
        ]
          .filter(Boolean)
          .join(' '),
      );
      return haystack.includes(q);
    });
  }, [rows, query, statusFilter, emails]);

  const allShownSelected = visible.length > 0 && visible.every((l) => selected.has(l.id));
  const toggleAllShown = () =>
    setSelected(allShownSelected ? new Set() : new Set(visible.map((l) => l.id)));


  const filterBtn = (value: StatusFilter, label: string) => (
    <button
      key={value}
      onClick={() => setStatusFilter(value)}
      className={`rounded-lg px-3 py-2 text-xs font-bold transition-colors ${
        statusFilter === value
          ? 'bg-brand-navy text-white'
          : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-extrabold text-slate-900">{t('admin.moderationTitle')}</h2>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">{rows.length}</span>
      </div>
      <p className="mt-1 text-sm text-slate-500">{t('admin.moderationSub')}</p>
      <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600">
        {t('admin.modHint')}
      </p>

      {/* Search + status filter + bulk action bar */}
      <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('admin.modSearch')}
            className="w-full rounded-lg border border-slate-300 py-2.5 pl-9 pr-9 text-sm focus:border-brand-blue focus:outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              aria-label={t('common.clear')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {filterBtn('all', t('admin.modFilterAll'))}
          {filterBtn('published', t('admin.modFilterPublished'))}
          {filterBtn('removed', t('admin.modFilterRemoved'))}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600">
          <input
            type="checkbox"
            checked={allShownSelected}
            onChange={toggleAllShown}
            className="h-4 w-4 rounded border-slate-300"
          />
          {t('admin.modSelectAll')}
        </label>
        <span className="text-xs text-slate-400">
          {t('admin.modShowing', { n: visible.length, total: rows.length })}
        </span>
        {selected.size > 0 && (
          <>
            <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-white">
              {t('admin.modSelected', { n: selected.size })}
            </span>

            <button
              onClick={bulkUnpublish}
              disabled={busy === 'bulk'}
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs font-bold text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              <EyeOff className="h-3.5 w-3.5" /> {t('admin.modBulkUnpublish')}
            </button>
          </>
        )}
      </div>

      {msg && (
        <p className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">{msg}</p>
      )}

      <div className="mt-5 space-y-4">
        {rows.length === 0 && (
          <p className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
            {t('common.none')}
          </p>
        )}
        {rows.length > 0 && visible.length === 0 && (
          <p className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
            {t('admin.modNoMatch')}
          </p>
        )}

        {visible.map((l) => {
          const photos: string[] = (l.photos || []).filter(Boolean);
          const cover = coverImageFor(l);
          const removed = l.status === 'removed';
          const pii = piiHits(l);
          const ownerEmail = emails[l.owner_id];
          return (
            <div
              key={l.id}
              className={`rounded-2xl border bg-white p-5 ${
                selected.has(l.id)
                  ? 'border-brand-blue ring-2 ring-blue-100'
                  : removed
                  ? 'border-red-200 bg-red-50/40'
                  : 'border-slate-200'
              }`}
            >
              <div className="flex flex-col gap-4 sm:flex-row">
                <input
                  type="checkbox"
                  checked={selected.has(l.id)}
                  onChange={() => toggle(l.id)}
                  aria-label={l.title}
                  className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300"
                />
                <img
                  src={cover.src}
                  alt=""
                  loading="lazy"
                  className="h-24 w-full shrink-0 rounded-xl object-cover sm:w-36"
                />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-bold uppercase text-white">
                      {tradeLabel(l.trade, lang)}
                    </span>
                    <span
                      className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase ${
                        removed ? 'bg-red-100 text-red-700' : 'bg-emerald-50 text-emerald-700'
                      }`}
                    >
                      {statusLabel(l.status)}
                    </span>

                    {pii.length > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold uppercase text-amber-800">
                        <ShieldAlert className="h-3 w-3" /> {t('admin.piiFlag')}
                      </span>
                    )}
                    <span className="text-xs text-slate-400">
                      {photos.length} {t('admin.photos')}
                    </span>
                  </div>
                  <h3 className="mt-2 font-bold text-slate-900">{l.title}</h3>
                  <p className="text-sm text-slate-500">
                    {l.city}, {l.state} · {l.source_type === 'gc' ? t('jobs.byGc') : t('jobs.byHomeowner')}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-400">
                    {ownerEmail ? `${t('admin.modOwner')}: ${ownerEmail} · ` : ''}
                    {l.created_at ? new Date(l.created_at).toLocaleDateString() : ''}
                  </p>
                  {pii.length > 0 && (
                    <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold leading-relaxed text-amber-900">
                      {t('admin.piiBody')} — {pii.join(' · ')}
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 items-start">
                  {removed ? (
                    <button
                      onClick={() => setStatus(l, 'published')}
                      disabled={busy === l.id}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 px-3 py-2 text-xs font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                    >
                      <Check className="h-3.5 w-3.5" /> {t('admin.restoreListing')}
                    </button>
                  ) : (
                    <button
                      onClick={() => setStatus(l, 'removed')}
                      disabled={busy === l.id}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-2 text-xs font-bold text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      <EyeOff className="h-3.5 w-3.5" /> {t('admin.unpublish')}
                    </button>
                  )}
                </div>
              </div>

              {photos.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-3 border-t border-slate-100 pt-4">
                  {photos.map((url, i) => (
                    <div key={url} className="relative">
                      <img src={url} alt="" loading="lazy" className="h-20 w-24 rounded-lg object-cover" />
                      {i === 0 && (
                        <span className="absolute left-1 top-1 rounded bg-slate-900/80 px-1.5 py-0.5 text-[10px] font-bold text-white">
                          {t('photos.cover')}
                        </span>
                      )}
                      <button
                        onClick={() => removePhoto(l, url)}
                        disabled={busy === l.id}
                        title={t('admin.removePhoto')}
                        aria-label={t('admin.removePhoto')}
                        className="absolute -right-2 -top-2 rounded-full bg-red-600 p-1.5 text-white shadow hover:bg-red-700 disabled:opacity-50"
                      >
                        <ImageOff className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const RoleAudit: React.FC = () => {
  const { t } = useI18n();
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    supabase
      .from('mkt_profiles')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data }) => setRows(data || []));
  }, []);

  const correct = async (u: Row, role: UserType) => {
    if (role === u.user_type) return;
    setBusy(u.id);
    setMsg('');
    const { data, error } = await supabase.functions.invoke('admin-set-role', {
      body: { userId: u.id, role },
    });
    if (error || data?.error) {
      setMsg(data?.error || error?.message || t('admin.roleFixError'));
    } else {
      setRows((p) => p.map((x) => (x.id === u.id ? { ...x, user_type: role } : x)));
      setMsg(t('admin.roleFixed'));
    }
    setBusy(null);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <ShieldAlert className="h-5 w-5 text-orange-500" />
        <h2 className="text-xl font-extrabold text-slate-900">{t('admin.roleAuditTitle')}</h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">{t('admin.roleAuditSub')}</p>
      {msg && <p className="mt-3 rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700">{msg}</p>}

      <div className="mt-6 space-y-8">
        {ROLES.map((role) => {
          const group = rows.filter((u) => u.user_type === role);
          return (
            <div key={role}>
              <h3 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-slate-900">
                <UserCog className="h-4 w-4 text-slate-400" />
                {t(`role.${role}`)}
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">{group.length}</span>
              </h3>
              <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
                {group.length === 0 ? (
                  <p className="p-6 text-center text-sm text-slate-500">{t('common.none')}</p>
                ) : (
                  <table className="w-full min-w-[640px] text-left text-sm">
                    <tbody className="divide-y divide-slate-100">
                      {group.map((u) => (
                        <tr key={u.id}>
                          <td className="px-4 py-3 font-semibold text-slate-800">
                            {u.company_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{u.email}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap justify-end gap-1.5">
                              {ROLES.filter((r) => r !== u.user_type).map((r) => (
                                <button
                                  key={r}
                                  onClick={() => correct(u, r)}
                                  disabled={busy === u.id}
                                  className="rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                >
                                  {t('admin.makeRole')} {t(`role.${r}`)}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ListingsModeration;
